import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];
const pad = (n) => String(n).padStart(2, "0");

export const categories = [
  { key: "print-design", title: "Print Design", desc: "Selected print and communication design projects.", tone: "gold" },
  { key: "packaging-design", title: "Packaging Design", desc: "Packaging systems and product presentation design.", tone: "blue" },
  { key: "website-design", title: "Website Design", desc: "Digital experiences and website design projects.", tone: "red" },
  { key: "social-media-creatives", title: "Social Media Creatives", desc: "Scroll-stopping campaign and social media creatives.", tone: "green" },
];

export const catTitle = (key) => categories.find((c) => c.key === key)?.title || key;

export const sortedProjects = (list) => [...list].sort((a, b) => (Number(b.featured) - Number(a.featured)) || ((a.order || 0) - (b.order || 0)));

function useLockScroll() {
  useEffect(() => {
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    return () => { window.__lenis?.start(); document.body.style.overflow = ""; };
  }, []);
}

function Detail({ list, index, onNav, onClose }) {
  useLockScroll();
  const p = list[index];
  if (!p) return null;
  return (
    <div className="detail-overlay" data-lenis-prevent data-testid="project-detail">
      <div className="detail-top">
        <button className="text-link" data-testid="detail-back-button" onClick={onClose}><ArrowLeft size={15} /> Back to gallery</button>
        <div className="detail-nav">
          <button data-testid="detail-prev-button" disabled={index === 0} onClick={() => onNav(index - 1)}><ArrowLeft size={15} /> Prev</button>
          <span data-testid="detail-position">{pad(index + 1)} / {pad(list.length)}</span>
          <button data-testid="detail-next-button" disabled={index === list.length - 1} onClick={() => onNav(index + 1)}>Next <ArrowRight size={15} /></button>
        </div>
      </div>
      <div className="detail-image"><img key={p.id} src={`${API}/files/${p.storage_path}`} alt={p.title} /></div>
      <div className="detail-meta">
        <h3>{p.title}</h3>
        <div className="detail-facts">
          {p.client && <span>Client — {p.client}</span>}
          {p.year && <span>Year — {p.year}</span>}
          <span>{catTitle(p.category)}</span>
        </div>
        {p.description && <p>{p.description}</p>}
      </div>
    </div>
  );
}

function Grid({ list, cat, onOpen }) {
  return (
    <div className="gallery-grid">
      {list.map((p, i) => (
        <button className="gallery-item" data-testid={`gallery-item-${i + 1}`} key={p.id} onClick={() => onOpen(i)}>
          <div className="gallery-image"><img src={`${API}/files/${p.storage_path}`} alt={p.title} loading="lazy" /></div>
          <h4>{p.title}</h4>
          <div className="meta">{[p.client, p.year].filter(Boolean).join(" · ") || catTitle(p.category)}</div>
        </button>
      ))}
    </div>
  );
}

export function CategoryGallery({ category, projects, onClose }) {
  useLockScroll();
  const [detail, setDetail] = useState(null);
  const list = sortedProjects(projects);
  return (
    <motion.div className="gallery-overlay" data-lenis-prevent data-testid="category-gallery" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
      <div className="gallery-head">
        <div>
          <span className="eyebrow">Portfolio — {pad(list.length)} project{list.length !== 1 ? "s" : ""}</span>
          <h2>{category.title}</h2>
          <p className="gallery-desc">{category.desc}</p>
        </div>
        <button className="text-link" data-testid="gallery-back-button" onClick={onClose}><ArrowLeft size={15} /> All categories</button>
      </div>
      {list.length === 0 ? <p className="gallery-empty" data-testid="gallery-empty">Projects for this category are on their way.</p> : <Grid list={list} cat={category} onOpen={setDetail} />}
      {detail !== null && <Detail list={list} index={detail} onNav={setDetail} onClose={() => setDetail(null)} />}
    </motion.div>
  );
}

export function Archive({ projects, onClose }) {
  useLockScroll();
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const list = sortedProjects(filter === "all" ? projects : projects.filter((p) => p.category === filter));
  return (
    <motion.div className="gallery-overlay" data-lenis-prevent data-testid="archive-view" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
      <div className="gallery-head">
        <div>
          <span className="eyebrow">Archive — {pad(list.length)} project{list.length !== 1 ? "s" : ""}</span>
          <h2>All work.</h2>
          <p className="gallery-desc">The complete VirtualCreato portfolio across every discipline.</p>
        </div>
        <button className="text-link" data-testid="archive-back-button" onClick={onClose}><ArrowLeft size={15} /> Back to portfolio</button>
      </div>
      <div className="filter-pills" data-testid="archive-filters">
        <button className={filter === "all" ? "active" : ""} data-testid="filter-all" onClick={() => { setFilter("all"); setDetail(null); }}>All</button>
        {categories.map((c) => <button key={c.key} className={filter === c.key ? "active" : ""} data-testid={`filter-${c.key}`} onClick={() => { setFilter(c.key); setDetail(null); }}>{c.title}</button>)}
      </div>
      {list.length === 0 ? <p className="gallery-empty" data-testid="archive-empty">Projects are on their way.</p> : <Grid list={list} onOpen={setDetail} />}
      {detail !== null && <Detail list={list} index={detail} onNav={setDetail} onClose={() => setDetail(null)} />}
    </motion.div>
  );
}
